import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Button,
  Flex,
  Grid,
  Label,
  Text,
  FileUpload,
  FormHeading,
  ErrorMsg,
  Toastify,
} from "atoms";
import { DateInput } from "atoms/DateInput";
import { FormInput } from "molecules";
import { FormCheckbox, FormRadio, FormSelect } from "molecules/FormInput";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import { optionFetch } from "utils/utilities";
import { usePropsoch } from "contexts/Propsoch";
import { useParams } from "react-router-dom";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Project name is required")
    .typeError("Project name is required")
    .max(60, "Project name should not exceed 60 characters"),
  tagLine: Yup.string()
    .required("Project tag line is required")
    .typeError("Project tag line is required"),
  images: Yup.array()
    // .of(Yup.string())
    .min(1, "Add minimum 1 image")
    .typeError("Project image is required")
    .required("Project image is required"),
  statusId: Yup.string()
    .typeError("Project status is required")
    .required("Project status is required"),
  locationId: Yup.object().shape({
    label: Yup.string()
      .typeError("Primary market is required")
      .required("Primary market is required"),
    value: Yup.string()
      .typeError("Primary market is required")
      .required("Primary market is required"),
  }),
  micromarketId: Yup.string()
    .typeError("Micro market is required")
    .required("Micro market is required"),
  possessionDate: Yup.string()
    .typeError("Possession date is required")
    .required("Possession date is required"),
  types: Yup.array()
    // .of(Yup.string())
    .min(1, "Choose a project type")
    .max(1, "Only one project type allowed")
    .typeError("Project type is required")
    .required("Project type is required"),
  developerId: Yup.object().shape({
    label: Yup.string()
      .typeError("Developer is required")
      .required("Developer is required"),
    value: Yup.string()
      .typeError("Developer is required")
      .required("Developer is required"),
  }),
  typologies: Yup.array()
    // .of(Yup.string())
    .min(1, "Choose a typology")
    .typeError("Typology is required")
    .required("Typology is required"),
  propertyTotalNumberOfUnits: Yup.number()
    .min(0, "Value should be greater than 0")
    .typeError("Invalid number")
    .required("Project total units/flats is required"),
  reraNumber: Yup.string()
    .typeError("RERA number is required")
    .max(60, "RERA number should not exceed 60 characters")
    .required("RERA number is required"),
  propertyLandArea: Yup.number()
    .min(0, "Value should be greater than 0")
    .typeError("Invalid number")
    .required("Project land area is required"),
  propertyVideoUrl: Yup.string()
    .url()
    .typeError("Property video url is required")
    .required("Property video url is required"),
});

export const BasicInfoForm = ({ onNext, bindHitOnSave, fetchProperty }) => {
  const {
    actions: {
      createPropertyListingBasicInfo,
      getPropertyListingBasicInfo,
      updatePropertyListingBasicInfo,
    },
  } = usePropsoch();

  const { id } = useParams();

  // fetch property
  const [property, setProperty] = useState(null);

  useEffect(() => {
    id &&
      getPropertyListingBasicInfo(id)?.then((data) => {
        setProperty(data);

        // reformat type
        data &&
          setProperty((prev) => {
            const types = data?.types?.map((item) => item?.type_id);
            types?.[0] &&
              optionFetch(
                `/typologies/get?pt=${types?.[0]}`,
                "name",
                "id"
              ).then((data) => {
                setTypologies(data);
              });
            return {
              ...prev,
              types,
              typologies: data?.typologies?.map((item) => item?.typology_id),
            };
          });

        // reformat location
        data &&
          optionFetch("/locations/get", "name", "id").then((optData) => {
            const location = optData?.find(
              (item) => item?.value === data?.locationId
            );
            setProperty((prev) => ({ ...prev, locationId: location }));
          });

        // data &&
        //   optionFetch("/micromarkets/get", "name", "id").then((optData) => {
        //     const micromarket = optData?.find(
        //       (item) => item?.value === data?.micromarketId
        //     );
        //     setProperty((prev) => ({ ...prev, micromarketId: micromarket }));
        //   });

        data &&
          optionFetch("/developers/get", "name", "id").then((optData) => {
            const developer = optData?.find(
              (item) => item?.value === data?.developerId
            );
            setProperty((prev) => ({ ...prev, developerId: developer }));
          });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // submit
  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = useCallback(
    (values) => {
      setSubmitLoading(true);
      if (values?.dirty) {
        if (id) {
          updatePropertyListingBasicInfo(values, id)
            .then(() => {
              Toastify("success", "Basic info updated");
              fetchProperty()?.then(() => {
                onNext();
              });
            })
            .catch((e) => {
              Toastify("error", "Basic info updated");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        } else {
          createPropertyListingBasicInfo(values)
            .then((data) => {
              Toastify("success", "Basic info created");
              fetchProperty()?.then(() => {
                onNext(data?.id);
              });
            })
            .catch((e) => {
              Toastify("error", "Basic info not created");
            })
            .finally(() => {
              setSubmitLoading(false);
            });
        }
      } else {
        if (id) {
          onNext();
        }
      }
    },
    [
      createPropertyListingBasicInfo,
      id,
      updatePropertyListingBasicInfo,
      onNext,
      fetchProperty,
    ]
  );

  // status, type & typology fetch
  const [status, setStatus] = useState(null);
  const [type, setType] = useState([]);
  const [typologies, setTypologies] = useState([]);
  const [micromarkets, setMicromarkets] = useState([]);
  useEffect(() => {
    // status
    optionFetch("/propertystatus/get", "name", "id").then((data) => {
      setStatus(data);
    });
    // type
    optionFetch("/propertytypes/get", "name", "id").then((data) => {
      setType(data);
    });
    // // typology
    // optionFetch("/typologies/get", "name", "id").then((data) => {
    //   setTypologies(data);
    // });
    // typology
    optionFetch("/micromarkets/get", "name", "id").then((data) => {
      setMicromarkets(data);
    });
  }, []);

  return (
    <Formik
      initialValues={{
        name: "",
        tagLine: "",
        images: [],
        statusId: "",
        locationId: "",
        micromarketId: "",
        possessionDate: "",
        types: "",
        developerId: "",
        typologies: [],
        propertyTotalNumberOfUnits: "",
        phaseTotalNumberOfUnits: "",
        reraNumber: "",
        propertyLandArea: "",
        phaseLandArea: "",
        propertyVideoUrl: "",
        ...(id && property),
      }}
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      enableReinitialize
    >
      {({ values, setFieldValue, submitForm, errors, touched, dirty }) => {
        bindHitOnSave(() => {
          setFieldValue("dirty", dirty);
          submitForm();
        });

        return (
          <Form>
            <Grid gridGap="2.4rem">
              <FormHeading text="Basic Info" />
              <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                <Grid gridRowGap="1.6rem">
                  <Label text="Project Name" req />
                  <FormInput
                    name="name"
                    type="text"
                    placeholder="Enter Project Name"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Tag Line" req />
                  <FormInput
                    name="tagLine"
                    type="text"
                    placeholder="Enter Tag Line"
                    req
                  />
                </Grid>
              </Grid>
              <Box>
                <Label text="Upload Project Images" mb="1.6rem" req />
                <FileUpload
                  files={values?.images || []}
                  onUpload={(locations) => {
                    const newProjectImages = [
                      ...(values?.images
                        ? JSON.parse(JSON.stringify(values?.images)) || []
                        : []),
                      ...locations?.map((i) => ({
                        image: i,
                        description: "",
                        alt: "",
                      })),
                    ];

                    setFieldValue("images", newProjectImages);
                  }}
                  isDesc
                  onSubmitDescription={({ description, alt, file }) => {
                    setFieldValue(
                      `images.[${file?.index}].description`,
                      description
                    );
                    setFieldValue(`images.[${file?.index}].alt`, alt);
                  }}
                  onCross={(index) => {
                    const currProjectImages = [
                      ...(values?.images
                        ? JSON.parse(JSON.stringify(values?.images)) || []
                        : []),
                    ];
                    const total = currProjectImages?.length;

                    const newProjectImages = [
                      ...currProjectImages?.slice(0, index),
                      ...currProjectImages?.slice(index + 1, total),
                    ];

                    setFieldValue("images", newProjectImages);
                  }}
                />
                {touched?.images && errors?.images && (
                  <ErrorMsg text={errors?.images} />
                )}
              </Box>
              <Label text="Project Status" req />
              <Grid
                // gridAutoFlow="column"
                gridTemplateColumns="repeat(5,1fr)"
                justifyContent="start"
                alignItems="center"
                gridGap="3rem"
                // mb="1rem"
              >
                {status?.map((item, index) => (
                  <FormRadio
                    key={index}
                    name="statusId"
                    label={item?.label}
                    value={item?.value}
                  />
                ))}
              </Grid>
              {touched?.statusId && errors?.statusId && (
                <ErrorMsg text={errors?.statusId} />
              )}

              <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                <Grid gridRowGap="1.6rem">
                  <Label text="Primary Market" req />
                  <FormSelect
                    name="locationId"
                    placeholder="Select drop down"
                    loadOptions={(inputString) =>
                      optionFetch("/locations/get", "name", "id", inputString)
                    }
                    isOptionLoadAsync
                    defaultOptions
                    cacheOptions
                  />
                </Grid>
              </Grid>
              <Grid
                gridTemplateColumns="1fr 1fr"
                gridColumnGap="2.4rem"
                gridRowGap="1.6rem"
              >
                {/* <Grid gridRowGap="1.6rem"> */}
                <Label text="Micro market" req />
                <Label text="Possesion" req />
                <FormSelect
                  name="micromarketId"
                  placeholder="Select drop down"
                  options={micromarkets?.filter(
                    (i) => i?.data?.locationId === values?.locationId?.value
                  )}
                  // loadOptions={(inputString) =>
                  //   optionFetch(
                  //     "/micromarkets/get",
                  //     "name",
                  //     "id",
                  //     inputString,
                  //     (res) => {
                  //       return res?.filter(
                  //         (i) =>
                  //           i?.data?.locationId === values?.locationId?.value
                  //       );
                  //     }
                  //   )
                  // }
                  // isOptionLoadAsync
                  // defaultOptions
                />
                {/* </Grid>
                <Grid gridRowGap="1.6rem"> */}
                {/* <Label text="Possesion" req /> */}
                <DateInput
                  name="possessionDate"
                  placeholderText="Select Start Date"
                  selected={
                    values?.possessionDate
                      ? new Date(values?.possessionDate)
                      : ""
                  }
                  onChange={(e) => {
                    setFieldValue("possessionDate", e?.toISOString());
                  }}
                  error={touched?.possessionDate && errors?.possessionDate}
                />

                {/* </Grid> */}
              </Grid>
              <Label text="Project Type" req />
              <Grid
                // gridAutoFlow="column"
                gridTemplateColumns="repeat(5,1fr)"
                gridGap="3rem"
                justifyContent="start"
                mb="1rem"
              >
                {type?.map((item, index) => (
                  <Flex aI="center" key={index}>
                    <FormCheckbox
                      name="types"
                      value={item?.value}
                      height="1.8rem"
                      isMulti={false}
                      onClick={() => {
                        optionFetch(
                          `/typologies/get?pt=${item?.value}`,
                          "name",
                          "id"
                        ).then((data) => {
                          setTypologies(data);
                        });
                      }}
                    />
                    <Text color="neutral.700">{item?.label}</Text>
                  </Flex>
                ))}
              </Grid>
              {touched?.types && errors?.types && (
                <ErrorMsg text={errors?.types} />
              )}
              <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                <Grid gridRowGap="1.6rem">
                  <Label text="Developer" req />
                  <FormSelect
                    name="developerId"
                    placeholder="Select drop down"
                    loadOptions={(inputString) =>
                      optionFetch("/developers/get", "name", "id", inputString)
                    }
                    isOptionLoadAsync
                    defaultOptions
                  />
                </Grid>
              </Grid>
              <Label text="Typology" req />
              {typologies?.length === 0 && (
                <Label color="neutral.700" text="Select a project type" req />
              )}
              <Grid
                // gridAutoFlow="column"
                gridTemplateColumns="repeat(5,1fr)"
                gridGap="3rem"
                justifyContent="start"
                // mb="1rem"
              >
                {typologies?.map((item, index) => (
                  <Flex aI="center" key={index}>
                    <FormCheckbox
                      name="typologies"
                      value={item?.value}
                      height="1.8rem"
                    />
                    <Text color="neutral.700">{item?.label}</Text>
                  </Flex>
                ))}
              </Grid>
              {touched?.typologies && errors?.typologies && (
                <ErrorMsg text={errors?.typologies} />
              )}
              {/* <Flex
                alignItems="center"
                color="secondary.700"
                cursor="pointer"
                onClick={() => {
                  setTypologyModalVisible({ visible: true });
                }}
              >
                <MemoAdd height="1.3rem" width="1.3rem" />
                <Text
                  color="secondary.700"
                  fontFamily="NeometricBold"
                  ml="1rem"
                >
                  Add More
                </Text>
              </Flex> */}
              <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                <Grid gridRowGap="1.6rem">
                  <Label text="Project Total number of units/ flats" req />
                  <FormInput
                    name="propertyTotalNumberOfUnits"
                    placeholder="Enter total number of units/ flats"
                    // type="number"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Phase total number of units" />
                  <FormInput
                    name="phaseTotalNumberOfUnits"
                    placeholder="Enter total number of units"
                    // type="number"
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="RERA Number" req />
                  <FormInput
                    name="reraNumber"
                    placeholder="Enter Number"
                    // type="number"
                    req
                  />
                </Grid>
              </Grid>
              <Grid gridTemplateColumns="1fr 1fr" gridGap="2.4rem">
                <Grid gridRowGap="1.6rem">
                  <Label text="Project Land Area" req />
                  <FormInput
                    name="propertyLandArea"
                    placeholder="Enter Acres"
                    // type="number"
                    req
                  />
                </Grid>
                <Grid gridRowGap="1.6rem">
                  <Label text="Phase Land Area" />
                  <FormInput
                    name="phaseLandArea"
                    placeholder="Enter in Acres"
                    // type="number"
                  />
                </Grid>
              </Grid>
              <Grid gridRowGap="1.6rem">
                <Label text="Property Video Url" req />
                <FormInput
                  name="propertyVideoUrl"
                  placeholder="Enter Url"
                  // type="number"
                  req
                />
              </Grid>

              <Button
                my="2rem"
                px="3rem"
                width="fit-content"
                variant="primary"
                height="4.8rem"
                type="submit"
                loading={submitLoading}
                onClick={() => {
                  setFieldValue("dirty", dirty);
                  submitForm();
                }}
              >
                Next
              </Button>
            </Grid>

            {/* <AddNewModal
              isOpen={isTypologyModalVisible?.visible}
              onClose={() => setTypologyModalVisible(false)}
              title={"Typology"}
              loading={isTypologyModalVisible?.loading}
              error={isTypologyModalVisible?.error}
              resetError={() =>
                setTypologyModalVisible((prev) => ({
                  ...prev,
                  error: "",
                }))
              }
              onSubmit={({ name }) => {
                setTypologyModalVisible((prev) => ({ ...prev, loading: true }));
                (async (name) => {
                  try {
                    const reqData = { name };
                    const { data } = await api("/typologies/add", {
                      method: "post",
                      data: reqData,
                    });

                    return data;
                  } catch (e) {
                    throw e;
                  }
                })(name)
                  ?.then(() => {})
                  ?.catch((e) =>
                    setTypologyModalVisible((prev) => ({
                      ...prev,
                      loading: false,
                      error: e?.message,
                    }))
                  )
                  .finally(() => {
                    setTypologyModalVisible(false);
                    // typology
                    optionFetch("/typologies/get", "name", "id").then(
                      (data) => {
                        setTypologies(data);
                      }
                    );
                  });
              }}
            /> */}
          </Form>
        );
      }}
    </Formik>
  );
};
/**
 * 
 {
    "name": "UNA",
    "images": [
        "https://propsoch-bucket.s3-ap-south-1.amazonaws.com/alex-wong-l5Tzv1alcps-unsplash.jpg",
        "https://propsoch-bucket.s3-ap-south-1.amazonaws.com/donny-jiang-42gFAgdIUC8-unsplash.jpg",
        "https://propsoch-bucket.s3-ap-south-1.amazonaws.com/iltun-huseynli-rfklsTwIYBM-unsplash.jpg",
        "https://propsoch-bucket.s3-ap-south-1.amazonaws.com/jimmy-chang-ACt8ycSzpdE-unsplash.jpg"
    ],
    "statusId": "ongoing",
    "locationId": {
        "label": "Pune",
        "value": 12,
        "data": {
            "id": 12,
            "name": "Pune",
            "createdBy": "3",
            "updatedBy": "",
            "createdAt": "2022-12-13T12:17:19.000Z",
            "updatedAt": "2022-12-13T12:17:19.000Z"
        }
    },
    "micromarketId": {
        "label": "Shintaro",
        "value": 7,
        "data": {
            "id": 7,
            "name": "Shintaro",
            "createdBy": "3",
            "updatedBy": "3",
            "createdAt": "2022-12-13T12:15:13.000Z",
            "updatedAt": "2022-12-13T12:32:35.000Z",
            "LocationModelId": null,
            "locationId": 1,
            "locations": {
                "id": 1,
                "name": "Bangalore",
                "createdBy": "1",
                "updatedBy": "1",
                "createdAt": "2022-12-13T07:15:50.000Z",
                "updatedAt": "2022-12-13T07:17:18.000Z"
            }
        }
    },
    "possessionDate": "2022-12-12T18:30:00.000Z",
    "types": {
        "apartment": true,
        "plots": true,
        "rowHouse": true,
        "township": false,
        "villa": false
    },
    "developerId": {
        "label": "Poncho",
        "value": 1,
        "data": {
            "id": 1,
            "name": "Poncho",
            "createdBy": "3",
            "updatedBy": "",
            "createdAt": "2022-12-13T12:39:54.000Z",
            "updatedAt": "2022-12-13T12:39:54.000Z",
            "LocationModelId": null,
            "locationId": 11,
            "locations": {
                "id": 11,
                "name": "Delhi",
                "createdBy": "3",
                "updatedBy": "",
                "createdAt": "2022-12-13T12:17:14.000Z",
                "updatedAt": "2022-12-13T12:17:14.000Z"
            }
        }
    },
    "typologies": {
        "oneBHK": false,
        "twoBhk": true,
        "threeBhk": true,
        "fourBhk": false,
        "fiveBhk": false
    },
    "propertyTotalNumberOfUnits": 10,
    "phaseTotalNumberOfUnits": 5,
    "reraNumber": 6566,
    "propertyLandArea": 100,
    "phaseLandArea": 50
  }
*/
